import { useState } from "react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
    ShoppingCart, Building2, Calendar as CalendarIcon, Check, Sparkles, DollarSign, ArrowRight, Tv,
    Users, Heart, Zap,
} from "lucide-react";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";

export default function Order() {
    const [orderType, setOrderType] = useState("rent");
    const [form, setForm] = useState({
        company_name: "", email: "", telephone: "", event_name: "", address: "", units: 1, notes: "",
    });
    const [date, setDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!form.company_name || !form.email || !form.telephone || !form.address) {
            toast.error("Please complete the required fields.");
            return;
        }
        setLoading(true);
        try {
            const payload = {
                ...form,
                order_type: orderType,
                units: Number(form.units) || 1,
                event_date: date ? date.toISOString() : null,
            };
            const { data } = await api.post("/orders", payload);
            setDone(true);
            toast.success(data.message);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Order failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white" data-testid="order-page">
            <Navbar />
            <main className="pt-24">
                {/* Hero */}
                <section className="relative overflow-hidden bg-[#0d0d10] text-white">
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(230,57,70,0.3),transparent_60%),radial-gradient(ellipse_at_bottom_left,rgba(247,127,0,0.25),transparent_60%)]" />
                    <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur text-xs font-mono tracking-[0.18em] text-orange-100 mb-6">
                            <ShoppingCart className="w-3 h-3 text-[var(--medx-orange)]" /> BUY · RENT · EARN
                        </div>
                        <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter max-w-5xl mb-6 text-white">
                            Own a kiosk. Rent one for a day. <span className="brand-gradient-text">Or profit from one.</span>
                        </h1>
                        <p className="text-xl text-gray-300 max-w-3xl leading-relaxed">
                            Medx AI Kiosks are available to companies, NGOs, clinics, pharmacies, doctors and event agencies —
                            for permanent installation, health & wellness days, expos and conferences.
                        </p>
                    </div>
                </section>

                {/* 3 banners: Sell / Rent / Earn */}
                <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {/* Buy */}
                        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--medx-red)] to-[#b91c1c] p-8 text-white" data-testid="order-banner-buy">
                            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
                            <Building2 className="w-10 h-10 mb-5" />
                            <div className="text-xs font-mono tracking-[0.22em] text-white/70 mb-2">OPTION 01 · BUY</div>
                            <h2 className="font-display text-3xl font-bold mb-3">Own a fully-branded unit.</h2>
                            <p className="text-white/85 text-sm leading-relaxed mb-5">
                                Perfect for hospitals, pharmacies, corporate HQs and NGOs. Kiosk is yours — with
                                full co-branding, on-site installation and 24-month warranty.
                            </p>
                            <ul className="space-y-2 text-sm mb-6">
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> Lifetime ownership + software updates</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> Custom branding & idle-screen content</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> 12-month free maintenance</li>
                            </ul>
                            <button onClick={() => { setOrderType("buy"); document.getElementById("order-form")?.scrollIntoView({ behavior: "smooth" }); }}
                                className="inline-flex items-center gap-2 bg-white text-[var(--medx-red)] font-semibold px-5 py-2.5 rounded-full hover:bg-white/90"
                                data-testid="order-banner-buy-cta">
                                Enquire to Buy <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Rent */}
                        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[var(--medx-orange)] to-[#c2410c] p-8 text-white" data-testid="order-banner-rent">
                            <div className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-white/10 blur-2xl" />
                            <CalendarIcon className="w-10 h-10 mb-5" />
                            <div className="text-xs font-mono tracking-[0.22em] text-white/70 mb-2">OPTION 02 · RENT</div>
                            <h2 className="font-display text-3xl font-bold mb-3">For a day, a week, a wellness season.</h2>
                            <p className="text-white/85 text-sm leading-relaxed mb-5">
                                Event companies, corporate wellness teams, expos and conferences rent kiosks
                                per-day. We deliver, install and collect. You get the data.
                            </p>
                            <ul className="space-y-2 text-sm mb-6">
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> From a single day to multi-month</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> Includes on-site technician</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0" /> Branded wellness report for attendees</li>
                            </ul>
                            <button onClick={() => { setOrderType("rent"); document.getElementById("order-form")?.scrollIntoView({ behavior: "smooth" }); }}
                                className="inline-flex items-center gap-2 bg-white text-[var(--medx-orange)] font-semibold px-5 py-2.5 rounded-full hover:bg-white/90"
                                data-testid="order-banner-rent-cta">
                                Book a Rental <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Earn */}
                        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 to-black p-8 text-white" data-testid="order-banner-earn">
                            <div className="absolute -right-10 -bottom-10 w-40 h-40 rounded-full bg-[var(--medx-orange)]/20 blur-2xl" />
                            <DollarSign className="w-10 h-10 mb-5 text-[var(--medx-orange)]" />
                            <div className="text-xs font-mono tracking-[0.22em] text-white/60 mb-2">OPTION 03 · EARN</div>
                            <h2 className="font-display text-3xl font-bold mb-3">Make money. Save time. Join the revolution.</h2>
                            <p className="text-white/85 text-sm leading-relaxed mb-5">
                                Host a Medx AI kiosk in your foot-traffic space. Earn from every scan, every
                                consult, and every ad played on the idle screen. Healthcare meets hospitality meets
                                hospitality-grade passive income.
                            </p>
                            <ul className="space-y-2 text-sm mb-6">
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-[var(--medx-orange)]" /> Revenue share per telemedicine consult</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-[var(--medx-orange)]" /> Passive income from idle-screen advertising</li>
                                <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-0.5 shrink-0 text-[var(--medx-orange)]" /> Zero hardware risk — we deploy & maintain</li>
                            </ul>
                            <button onClick={() => { setOrderType("rent"); document.getElementById("order-form")?.scrollIntoView({ behavior: "smooth" }); }}
                                className="inline-flex items-center gap-2 bg-[var(--medx-orange)] text-white font-semibold px-5 py-2.5 rounded-full hover:bg-[var(--medx-orange-vivid)]"
                                data-testid="order-banner-earn-cta">
                                Become a Host <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </section>

                {/* Benefits strip */}
                <section className="bg-[var(--medx-cream)] py-16">
                    <div className="max-w-7xl mx-auto px-6 md:px-12">
                        <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3 text-center">WHY HOST A MEDX AI KIOSK</div>
                        <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tighter text-center mb-12 max-w-3xl mx-auto">
                            Every kiosk is a wellness centre, a marketing channel, and a revenue stream — in one footprint.
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                            {[
                                { icon: Users, k: "Footfall", v: "Average 40-120 scans per kiosk, per day." },
                                { icon: Heart, k: "Trust", v: "Clinical-grade screening earns customer loyalty." },
                                { icon: Tv, k: "Ad revenue", v: "Idle-screen advertising in 30-60-90 second slots." },
                                { icon: Zap, k: "Zero downtime", v: "Remote monitoring. Local engineer within 24h." },
                            ].map((b, i) => {
                                const Icon = b.icon;
                                return (
                                    <div key={i} className="card-soft p-6">
                                        <Icon className="w-7 h-7 text-[var(--medx-orange)] mb-3" />
                                        <div className="font-display text-xl font-semibold mb-1">{b.k}</div>
                                        <div className="text-gray-600 text-sm leading-relaxed">{b.v}</div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* Order form */}
                <section id="order-form" className="max-w-4xl mx-auto px-6 md:px-12 py-20">
                    <div className="text-xs font-mono tracking-[0.22em] text-[var(--medx-red)] mb-3 text-center">RESERVATION FORM</div>
                    <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tighter text-center mb-3">
                        Tell us what you need.
                    </h2>
                    <p className="text-gray-600 text-center max-w-2xl mx-auto mb-10">
                        Submit below and our support team will contact you to confirm your order, pricing and delivery.
                    </p>

                    {done ? (
                        <div className="card-soft p-10 text-center" data-testid="order-success">
                            <div className="w-16 h-16 mx-auto rounded-full bg-orange-50 grid place-items-center mb-4">
                                <Sparkles className="w-8 h-8 text-[var(--medx-orange)]" />
                            </div>
                            <div className="font-display text-3xl font-bold mb-3">Order received.</div>
                            <p className="text-gray-600 max-w-md mx-auto mb-6">
                                Thanks our support team will contact you shortly to confirm your Order.
                            </p>
                            <button onClick={() => { setDone(false); setForm({ company_name: "", email: "", telephone: "", event_name: "", address: "", units: 1, notes: "" }); setDate(null); }}
                                className="btn-outline">
                                Submit another
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="card-soft p-7 md:p-10 space-y-5" data-testid="order-form-element">
                            {/* order type */}
                            <div>
                                <label className="text-xs font-mono tracking-[0.18em] text-gray-500 mb-2 block">I WANT TO *</label>
                                <div className="grid grid-cols-2 gap-3">
                                    <button type="button" onClick={() => setOrderType("buy")}
                                        className={`px-4 py-4 rounded-xl border-2 transition-all text-sm font-semibold ${orderType === "buy" ? "border-[var(--medx-red)] bg-red-50 text-[var(--medx-red)]" : "border-gray-200 hover:border-gray-300"}`}
                                        data-testid="order-type-buy">
                                        <Building2 className="w-5 h-5 mx-auto mb-1" /> Buy a unit
                                    </button>
                                    <button type="button" onClick={() => setOrderType("rent")}
                                        className={`px-4 py-4 rounded-xl border-2 transition-all text-sm font-semibold ${orderType === "rent" ? "border-[var(--medx-orange)] bg-orange-50 text-[var(--medx-orange)]" : "border-gray-200 hover:border-gray-300"}`}
                                        data-testid="order-type-rent">
                                        <CalendarIcon className="w-5 h-5 mx-auto mb-1" /> Rent for an event
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500">COMPANY / ORGANIZATION *</label>
                                    <input required type="text" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                                        className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                        data-testid="order-company" />
                                </div>
                                <div>
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500">EMAIL *</label>
                                    <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                                        className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                        data-testid="order-email" />
                                </div>
                                <div>
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500">TELEPHONE *</label>
                                    <input required type="tel" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                                        className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                        data-testid="order-telephone" />
                                </div>
                                <div>
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500">NUMBER OF UNITS</label>
                                    <input type="number" min="1" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })}
                                        className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                        data-testid="order-units" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500">NAME OF EVENT / DEPLOYMENT</label>
                                    <input type="text" value={form.event_name} onChange={(e) => setForm({ ...form, event_name: e.target.value })}
                                        placeholder={orderType === "rent" ? "e.g. 2026 Corporate Wellness Week" : "e.g. Head office reception"}
                                        className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                        data-testid="order-event-name" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500">ADDRESS *</label>
                                    <input required type="text" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                                        placeholder="Street, city, country"
                                        className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm"
                                        data-testid="order-address" />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500">{orderType === "rent" ? "EVENT / DELIVERY DATE" : "PREFERRED INSTALLATION DATE"}</label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <button type="button" className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 hover:border-[var(--medx-orange)] text-left text-sm flex items-center gap-2"
                                                data-testid="order-date-btn">
                                                <CalendarIcon className="w-4 h-4 text-[var(--medx-orange)]" />
                                                {date ? date.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" }) : <span className="text-gray-400">Pick a date…</span>}
                                            </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date(new Date().toDateString())} />
                                        </PopoverContent>
                                    </Popover>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs font-mono tracking-[0.18em] text-gray-500">NOTES</label>
                                    <textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                        placeholder="Anything else we should know — expected footfall, branding needs, etc."
                                        className="w-full mt-1 px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm resize-none"
                                        data-testid="order-notes" />
                                </div>
                            </div>

                            <button type="submit" disabled={loading} className="btn-primary w-full !py-4 disabled:opacity-60" data-testid="order-submit">
                                {loading ? "Submitting…" : "Submit Order Request"}
                            </button>
                            <p className="text-xs text-gray-400 text-center">
                                Your information goes directly to our support team — we'll reply within one business day.
                                Questions? <a href="mailto:info@newmoonmedia.co.za" className="underline hover:text-[var(--medx-red)]">info@newmoonmedia.co.za</a>
                            </p>
                        </form>
                    )}
                </section>
            </main>
            <Footer />
        </div>
    );
}
